import fetchApi from '../util/fetchApi';
import checkErrors from './checkErrors';
import { WFAccessToken } from './types';

export const MAX_DATA_REMARK_MESSAGE_LENGTH = 3000;

export type DataRemarkMessage = {
  id: number;
  dataRemarkId: number;
  message: string;
  createdBy: { id: number; name: string; organizationalUnit: { name: string } } | null;
  createdAt: string;
  attachmentId: number | null;
  attachmentName: string | null;
};

export type DataRemark = {
  category: string;
  createdAt: string;
  createdBy: { id: number; name: string } | null;
  description: string;
  fieldName: string[];
  fieldPath: string[];
  fieldValue: string[];
  fieldPreviousValue?: string[];
  id?: number;
  isResolved: boolean;
  message: string;
  resolutionNotes: string | null;
  resolvedAt: string | null;
  resolvedBy: { id: number; name: string } | null;
  response: string | null;
  responseFile: number | null;
  uweEntityId: number;
  messages: DataRemarkMessage[];
};

const getAccessTokenHeaders = (wfAccessToken: WFAccessToken) => {
  if (!wfAccessToken) return {};

  return {
    headers: {
      'Workflow-Token': wfAccessToken?.hash,
    },
  };
};

const DataRemarksApi = {
  async getDataRemarksByEntity({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`data-remarks/entity/${entityId}`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getDataRemarkById({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`data-remarks/${id}`, getAccessTokenHeaders(wfAccessToken)));
  },

  async createDataRemark({ remark, wfAccessToken }: { remark: DataRemark; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.post('data-remarks', {
        ...getAccessTokenHeaders(wfAccessToken),
        data: remark,
      })
    );
  },

  async updateDataRemark({ id, remark, wfAccessToken }: { id: number; remark: DataRemark; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.put(`data-remarks/${id}`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: { ...remark, id },
      })
    );
  },

  async respondToDataRemark({
    id,
    response,
    responseFile,
    respondedByUserId,
    wfAccessToken,
  }: {
    id: number;
    response: string;
    responseFile: number;
    respondedByUserId: number;
    wfAccessToken: WFAccessToken;
  }) {
    return checkErrors(
      await fetchApi.api.post(`data-remarks/${id}/respond`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: {
          response,
          responseFile,
          respondedByUserId,
        },
      })
    );
  },

  async resolveDataRemark({
    id,
    resolutionNotes,
    resolvedByUserId,
    wfAccessToken,
  }: {
    id: number;
    resolutionNotes: string;
    resolvedByUserId: number;
    wfAccessToken: WFAccessToken;
  }) {
    return checkErrors(
      await fetchApi.api.post(`data-remarks/${id}/resolve`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: {
          resolutionNotes,
          resolvedByUserId,
        },
      })
    );
  },

  async addDataRemarkMessage({ id, message, file, wfAccessToken }: { id: number; message: string; file?: File; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.post(`data-remarks/${id}/messages`, {
        ...getAccessTokenHeaders(wfAccessToken),
        formdata: {
          message,
          ...(file && { file }),
        },
      })
    );
  },

  async bulkResolve({
    ids,
    resolutionNotes,
    wfAccessToken,
  }: {
    ids: number[];
    resolutionNotes: string;
    wfAccessToken: WFAccessToken;
  }): Promise<{ resolved: number; skipped: number; results: DataRemark[] }> {
    return checkErrors(
      await fetchApi.api.post('data-remarks/bulk/resolve', {
        ...getAccessTokenHeaders(wfAccessToken),
        data: { ids, resolutionNotes },
      })
    );
  },

  async bulkComment({
    ids,
    message,
    attachmentId,
    wfAccessToken,
  }: {
    ids: number[];
    message: string;
    attachmentId?: number;
    wfAccessToken: WFAccessToken;
  }): Promise<{ commented: number; skipped: number; results: DataRemarkMessage[] }> {
    return checkErrors(
      await fetchApi.api.post('data-remarks/bulk/comment', {
        ...getAccessTokenHeaders(wfAccessToken),
        data: { ids, message, ...(attachmentId && { attachmentId }) },
      })
    );
  },
};

export default DataRemarksApi;
